// ─────────────────────────────────────────────────────────────────────────────
// handwriting_recognizer.ts — Stroke-based handwriting → text recognition
//
// Primary:  Google Handwriting Input API (free, no key, online)
// Fallback: Tesseract.js (offline, WASM)
//
// Usage:
//   const reco = new HandwritingRecognizer();
//   const text = await reco.recognize(strokes, { language: 'en' });
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint } from '@shared/notebook_model.js';

// ── Public types ─────────────────────────────────────────────────────────────

export interface HwrStroke {
  id: string;
  points: StrokePoint[];
}

export interface HwrOptions {
  /** BCP-47 language hint (default 'en'). */
  language?: string;
  /** Max candidates to return (default 5). */
  maxCandidates?: number;
}

export interface HwrResult {
  /** Best candidate text. */
  text: string;
  /** All returned candidates, best first. */
  candidates: string[];
  /** Which backend produced this result. */
  source: 'google' | 'tesseract' | 'none';
}

// ── Google Handwriting Input endpoint ────────────────────────────────────────
//
// This is the same endpoint used by Google Handwriting Input on Android /
// Chrome OS.  It accepts ink traces as arrays of [xs, ys, timestamps] and
// returns word/character candidates.
//
// POST https://inputtools.google.com/request?itc=<lang>-t-i0-handwrit&app=chromeos
// Body: JSON with { ink_content: [ { strokes: [ [xs, ys, ts], ... ] } ] }

const GOOGLE_HWR_URL = 'https://inputtools.google.com/request';

function buildGooglePayload(
  strokes: HwrStroke[],
  language: string,
): { url: string; body: string } {
  const inkStrokes: Array<[number[], number[], number[]]> = [];

  for (const stroke of strokes) {
    const xs: number[] = [];
    const ys: number[] = [];
    const ts: number[] = [];
    for (const p of stroke.points) {
      xs.push(Math.round(p.x));
      ys.push(Math.round(p.y));
      ts.push(Math.round(p.timestamp));
    }
    inkStrokes.push([xs, ys, ts]);
  }

  // Compute bounding box for the writing_area hint
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of strokes) {
    for (const p of s.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  const w = maxX - minX || 300;
  const h = maxY - minY || 120;

  const itc = `${language}-t-i0-handwrit`;
  const url = `${GOOGLE_HWR_URL}?itc=${encodeURIComponent(itc)}&app=chromeos`;

  const body = JSON.stringify({
    app_version: 0.4,
    api_level: '537.36',
    device: navigator.userAgent.slice(0, 60),
    input_type: 0,
    options: 'enable_pre_space',
    requests: [{
      writing_guide: { writing_area_width: w, writing_area_height: h },
      ink: inkStrokes,
      language,
    }],
  });

  return { url, body };
}

async function recognizeGoogle(
  strokes: HwrStroke[],
  language: string,
  maxCandidates: number,
): Promise<HwrResult | null> {
  if (strokes.length === 0) return null;

  const { url, body } = buildGooglePayload(strokes, language);

  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (!resp.ok) return null;

    const data = await resp.json();
    // Response shape: ["SUCCESS", [ ["<itc>", [candidate1, candidate2, ...]] ]]
    if (data[0] !== 'SUCCESS' || !Array.isArray(data[1])) return null;

    const candidates: string[] = data[1][0]?.[1] ?? [];
    if (candidates.length === 0) return null;

    return {
      text: candidates[0],
      candidates: candidates.slice(0, maxCandidates),
      source: 'google',
    };
  } catch {
    return null; // network error → fall through to offline
  }
}

// ── Tesseract.js fallback (lazy-loaded) ──────────────────────────────────────

let tesseractLoaded = false;
let tesseractWorker: any = null;

async function ensureTesseract(): Promise<boolean> {
  if (tesseractLoaded) return tesseractWorker !== null;

  try {
    // Dynamic import — bundler will code-split this
    const Tesseract = await import('tesseract.js');
    tesseractWorker = await Tesseract.createWorker('eng');
    tesseractLoaded = true;
    return true;
  } catch {
    tesseractLoaded = true; // don't retry
    return false;
  }
}

function rasterizeStrokes(strokes: HwrStroke[], padding = 16): ImageData | null {
  if (strokes.length === 0) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of strokes) {
    for (const p of s.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }

  const w = Math.ceil(maxX - minX) + padding * 2;
  const h = Math.ceil(maxY - minY) + padding * 2;
  if (w <= 0 || h <= 0 || w > 4096 || h > 4096) return null;

  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const s of strokes) {
    if (s.points.length < 2) continue;
    ctx.beginPath();
    ctx.moveTo(s.points[0].x - minX + padding, s.points[0].y - minY + padding);
    for (let i = 1; i < s.points.length; i++) {
      ctx.lineTo(s.points[i].x - minX + padding, s.points[i].y - minY + padding);
    }
    ctx.stroke();
  }

  return ctx.getImageData(0, 0, w, h);
}

async function recognizeTesseract(
  strokes: HwrStroke[],
  maxCandidates: number,
): Promise<HwrResult | null> {
  const ready = await ensureTesseract();
  if (!ready || !tesseractWorker) return null;

  const imageData = rasterizeStrokes(strokes);
  if (!imageData) return null;

  try {
    // Tesseract.js accepts ImageData directly
    const { data } = await tesseractWorker.recognize(imageData);
    const text = (data.text as string).trim();
    if (!text) return null;

    return {
      text,
      candidates: [text].slice(0, maxCandidates),
      source: 'tesseract',
    };
  } catch {
    return null;
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

export class HandwritingRecognizer {
  private _pending = new Map<string, AbortController>();

  /**
   * Recognize handwriting from stroke data.
   * Tries Google online first, falls back to Tesseract.js if offline.
   */
  async recognize(
    strokes: HwrStroke[],
    opts: HwrOptions = {},
  ): Promise<HwrResult> {
    const language = opts.language ?? 'en';
    const maxCandidates = opts.maxCandidates ?? 5;

    if (strokes.length === 0) {
      return { text: '', candidates: [], source: 'none' };
    }

    // Try Google first (fast, high quality)
    const google = await recognizeGoogle(strokes, language, maxCandidates);
    if (google) return google;

    // Fallback: Tesseract.js (offline OCR)
    const tess = await recognizeTesseract(strokes, maxCandidates);
    if (tess) return tess;

    return { text: '', candidates: [], source: 'none' };
  }

  /**
   * Debounced recognition keyed by an ID (e.g. blockId or pageId).
   * If called again with the same key before the previous resolves,
   * the previous call is effectively superseded.
   */
  async recognizeDebounced(
    key: string,
    strokes: HwrStroke[],
    opts: HwrOptions = {},
  ): Promise<HwrResult> {
    // Cancel previous pending for this key
    const prev = this._pending.get(key);
    if (prev) prev.abort();

    const ctrl = new AbortController();
    this._pending.set(key, ctrl);

    try {
      const result = await this.recognize(strokes, opts);
      // Only return if this call wasn't superseded
      if (ctrl.signal.aborted) {
        return { text: '', candidates: [], source: 'none' };
      }
      return result;
    } finally {
      if (this._pending.get(key) === ctrl) {
        this._pending.delete(key);
      }
    }
  }

  dispose(): void {
    for (const ctrl of this._pending.values()) ctrl.abort();
    this._pending.clear();
  }
}
